/**
 * Word (.docx) import plugin.
 *
 * Adds a toolbar button that lets the user pick a Word document, converts it
 * to HTML in the browser using mammoth.js, and inserts the result at the
 * current cursor position.
 *
 * Images embedded in the document are handled here rather than left for
 * TinyMCE's auto-upload: web-compatible images are uploaded to the BookStack
 * gallery and inserted as normal <img> tags, while formats the server cannot
 * accept (e.g. Word's EMF/WMF vector images) are dropped so they don't trigger
 * upload errors.
 *
 * mammoth.js is a large library (~600kB), so it is loaded lazily from
 * `public/libs/mammoth/` only the first time the button is used rather than on
 * every editor load.
 */

let mammothLoad = null;

// Image content types BookStack accepts as gallery uploads. Anything else
// (image/x-emf, image/x-wmf, image/emf, …) is skipped during import.
const supportedImageTypes = [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp',
    'image/avif', 'image/tiff', 'image/bmp',
];

/**
 * Lazily load mammoth.js, returning the global `mammoth` object.
 * @return {Promise<Object>}
 */
function loadMammoth() {
    if (window.mammoth) {
        return Promise.resolve(window.mammoth);
    }
    if (!mammothLoad) {
        mammothLoad = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = window.baseUrl('/libs/mammoth/mammoth.browser.min.js');
            script.onload = () => resolve(window.mammoth);
            script.onerror = () => {
                mammothLoad = null;
                reject(new Error('Failed to load Word import library'));
            };
            document.head.appendChild(script);
        });
    }
    return mammothLoad;
}

/**
 * Prompt the user to select a .docx file.
 * @return {Promise<File|null>}
 */
function pickFile() {
    return new Promise(resolve => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        input.style.display = 'none';
        input.addEventListener('change', () => {
            resolve(input.files[0] || null);
            input.remove();
        });
        document.body.appendChild(input);
        input.click();
    });
}

/**
 * Convert a base64 string into a Blob of the given content type.
 * @param {string} base64
 * @param {string} contentType
 * @return {Blob}
 */
function base64ToBlob(base64, contentType) {
    const byteString = atob(base64);
    const bytes = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i += 1) {
        bytes[i] = byteString.charCodeAt(i);
    }
    return new Blob([bytes], {type: contentType});
}

// Image content types that the server converts to PNG (via LibreOffice) before
// storing, since browsers/BookStack can't handle them directly.
const convertibleImageTypes = {
    'image/x-emf': 'emf',
    'image/emf': 'emf',
    'image/x-wmf': 'wmf',
    'image/wmf': 'wmf',
    'image/x-wmf;': 'wmf',
};

/**
 * Upload an extracted image to the BookStack gallery.
 * @param {Blob} blob
 * @param {string} fileName
 * @param {int} pageId
 * @return {Promise<string>} the uploaded image URL
 */
async function uploadImage(blob, fileName, pageId) {
    const formData = new FormData();
    formData.append('file', blob, fileName);
    formData.append('uploaded_to', pageId);
    const resp = await window.$http.post(window.baseUrl('/images/gallery'), formData);
    return resp.data.url;
}

/**
 * Send a vector image (EMF/WMF) to the server for conversion to PNG, then
 * return the stored gallery image URL.
 * @param {Blob} blob
 * @param {string} fileName
 * @param {int} pageId
 * @return {Promise<string>} the converted image URL
 */
async function convertImageOnServer(blob, fileName, pageId) {
    const formData = new FormData();
    formData.append('file', blob, fileName);
    formData.append('uploaded_to', pageId);
    const resp = await window.$http.post(window.baseUrl('/images/word-import'), formData);
    return resp.data.url;
}

/**
 * Build a mammoth image handler that uploads supported images and drops the
 * rest. Tracks how many images were skipped via the provided counter object.
 * @param {Object} mammoth
 * @param {int} pageId
 * @param {{skipped: number, count: number}} stats
 * @return {Object} mammoth image converter
 */
function buildImageConverter(mammoth, pageId, stats) {
    return mammoth.images.imgElement(async image => {
        const convertExt = convertibleImageTypes[image.contentType];
        const isSupported = supportedImageTypes.includes(image.contentType);
        if (!isSupported && !convertExt) {
            stats.skipped += 1;
            return {src: ''};
        }
        try {
            const base64 = await image.read('base64');
            const blob = base64ToBlob(base64, image.contentType);
            stats.count += 1;
            if (convertExt) {
                const url = await convertImageOnServer(blob, `word-image-${stats.count}.${convertExt}`, pageId);
                return {src: url};
            }
            const ext = image.contentType.split('/')[1].replace('jpeg', 'jpg');
            const url = await uploadImage(blob, `word-image-${stats.count}.${ext}`, pageId);
            return {src: url};
        } catch (err) {
            console.error('Word import: image upload failed', err);
            stats.skipped += 1;
            return {src: ''};
        }
    });
}

/**
 * Remove <img> tags that have no usable source from an HTML string.
 * @param {string} html
 * @return {string}
 */
function stripEmptyImages(html) {
    const wrap = document.createElement('div');
    wrap.innerHTML = html;
    wrap.querySelectorAll('img').forEach(img => {
        if (!img.getAttribute('src')) {
            img.remove();
        }
    });
    return wrap.innerHTML;
}

/**
 * @param {WysiwygConfigOptions} options
 * @param {Editor} editor
 */
function register(options, editor) {
    editor.ui.registry.addIcon('word-import', '<svg width="24" height="24"><path d="M6 2h8l4 4v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm7 1.5V7h3.5L13 3.5zM8.3 12l.9 4.2.95-4.2h1.7l.95 4.2.9-4.2H16l-1.5 6h-1.8l-.7-3.4-.7 3.4H9.5L8 12z"/></svg>');

    editor.addCommand('InsertWordDocument', async () => {
        const file = await pickFile();
        if (!file) {
            return;
        }

        editor.setProgressState(true);
        try {
            const mammoth = await loadMammoth();
            const arrayBuffer = await file.arrayBuffer();
            const stats = {count: 0, skipped: 0};
            const result = await mammoth.convertToHtml({arrayBuffer}, {
                convertImage: buildImageConverter(mammoth, options.pageId, stats),
            });
            editor.setProgressState(false);

            const html = stripEmptyImages(result.value || '');
            if (html.trim()) {
                editor.insertContent(html);
                if (stats.skipped > 0) {
                    window.$events.success(`Imported. ${stats.skipped} unsupported image(s) were skipped.`);
                }
            } else {
                window.$events.error('No content could be extracted from this document.');
            }
        } catch (err) {
            editor.setProgressState(false);
            console.error('Word import failed', err);
            window.$events.error('Failed to import the Word document.');
        }
    });

    editor.ui.registry.addButton('wordimport', {
        icon: 'word-import',
        tooltip: 'Import Word document',
        onAction() {
            editor.execCommand('InsertWordDocument');
        },
    });
}

/**
 * @param {WysiwygConfigOptions} options
 * @return {function(Editor)}
 */
export function getPlugin(options) {
    return editor => register(options, editor);
}
